import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { registerLocaleData } from '@angular/common';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatTableModule } from '@angular/material/table';
import { ToastrModule } from 'ngx-toastr';
import { ModalModule } from 'ngx-bootstrap/modal';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ButtonsModule } from 'ngx-bootstrap/buttons';

// modules
import { SharedModule } from './modules/shared/shared.module';

import { ToasterService } from './services/toaster.service';
import { AppComponent } from './app.component';
import { UserService } from './services/user.service';
import { HttpWrapperService } from './services/httpWrapper.service';
import { URLService } from './services/urls.service';
import { MenuOptionsService } from './services/menuoptions.service';
import { AppSideNavBarService } from './common/ui/app-side-nav-bar/app-side-nav-bar.service';
import { PageService } from './common/page/page.service';
import { httpInterceptorProviders } from './common/http/interceptors';
import { SignupService } from './services/signup.service';
import { InvoiceService } from './services/invoice.service';
import { GstPaymentService } from './services/gstPayment.service';
import { BeneficiaryService } from './services/beneficiary.service';
import { SubscriptionService } from './services/subscription.service';
import { UtilityPaymentService } from './services/utilitypayment.service';
import { ComplaintService } from './services/complaint.service';
import { CompanyPGService } from './services/companypg.service';
import { FilterModalService } from './filter/filter-modal.service';
import { AddCardService } from './services/add-card.service';
import { CardApplicationDetailsService } from './services/card-application-details.service';
import { CompanyConfigService } from './services/companyConfig.service';
import { CompanyPricingService } from './services/companyPricing.service';
import { ConnectedBankingService } from './services/connected-banking.service';
import { CreditProductService } from './services/credit-product.service';
import { DashboardCardsService } from './services/dashboard-cards.service';
import { EnkashCardsService } from './services/enkash-cards.service';
import { EnumApiService } from './services/enum-api.service';
import { ProductGuideService } from './services/product-guide.service';
import { LocalStorageService } from './services/localStorage.service';
import { FundTransferService } from './services/fund-transfer.service';
import { GstinService } from './services/gstin.service';
import { PaymentRequestService } from './services/payment-request.service';
import { PrepaidService } from './services/prepaid.service';
import { PurchaseOrderService } from './services/purchaseorder.service';
import { ProfileSetupServiceService } from './setup-profile/profile-setup-service.service';
import { SnackbarComponent } from './common/ui/snackbar/snackbar.component';
import { environment } from "../environments/environment";
import { CoreModule } from '../core/core.module';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { TitleChangerResolver } from './titleChanger.resolver';
import { TitleChangerService } from './titleChanger.service';


import { PopupLayoutModule } from './admin/confirmation-popup/popup-layout/popup-layout.module';
import { ConfigApiService } from '@app-service/config.service';
import { VendorOnBoardingService } from '@app-service/vendor-on-boarding.service';
import { HelpPopupEmployeeRewardsEmployeeComponent } from './admin/dashboard-cards/help-popup-employee-rewards-employee/help-popup-employee-rewards-employee.component';
import { HelpPopupEmployeeRewardsComponent } from './admin/dashboard-cards/help-popup-employee-rewards/help-popup-employee-rewards.component';
import { HelpPopupChannelIncentivesComponent } from './admin/dashboard-cards/help-popup channel-incentives/help-popup-channel-incentives.component';
import { HelpPopupChannelIncentivesUserComponent } from './admin/dashboard-cards/help-popup channel-incentives-user/help-popup-channel-incentives-user.component';
import { BillsOnBoardingService } from '@app-service/bills-on-boarding.service';
import localeEnIN from "@angular/common/locales/en-IN";
import { JoyrideModule } from "ngx-joyride";
import { WhatsappExpenseReceiptUploadComponent } from './whatsapp-expense-receipt-upload/whatsapp-expense-receipt-upload.component';
import { ExpenseApproverKgComponent } from './expense-approver-kg/expense-approver-kg.component';
import { InputAmountFieldModule } from '@app-module/shared/input-amount-field/input-amount-field.module';
import { NavigationService } from '@app-service/navigation.service';
import { CobrandedTncComponent } from './cobranded-tnc/cobranded-tnc.component';
import { NgIdleModule } from '@ng-idle/core';
import { ResponsiveService } from '@app-service/responsive.service';
import { VersionComponent } from './version/version.component';
import { ExportBannerComponent } from './services/export-service/export-banner.component';


export function getDatepickerConfig(): BsDatepickerConfig {
  return Object.assign(new BsDatepickerConfig(), {
    showWeekNumbers: false,
    dateInputFormat: 'DD-MMM-YYYY'
  });
}

@NgModule({ declarations: [
        AppComponent,
        SnackbarComponent,
        HelpPopupEmployeeRewardsComponent,
        HelpPopupEmployeeRewardsEmployeeComponent,
        HelpPopupChannelIncentivesComponent,
        HelpPopupChannelIncentivesUserComponent,
        WhatsappExpenseReceiptUploadComponent,
        ExpenseApproverKgComponent,
        CobrandedTncComponent,
    VersionComponent
    ],
    bootstrap: [
        AppComponent,
    ], imports: [NgIdleKeepaliveModule.forRoot(),
        NgIdleModule.forRoot(),
        BrowserModule,
        MatTableModule,
        AppRoutingModule,
        FormsModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot({
            closeButton: true,
            enableHtml: true,
            positionClass: 'toast-bottom-right',
            easing: 'ease-in'
        }),
        ModalModule.forRoot(),
        NgbModule,
        InfiniteScrollModule,
        CollapseModule.forRoot(),
        PopoverModule.forRoot(),
        TooltipModule.forRoot(),
        ReactiveFormsModule,
        SharedModule,
        FlexLayoutModule,
        BsDropdownModule.forRoot(),
        BsDatepickerModule.forRoot(),
        ButtonsModule.forRoot(),
        // ContentLoaderModule,
        CoreModule,
        PopupLayoutModule,
        InputAmountFieldModule,
        JoyrideModule.forRoot(),
        ExportBannerComponent], providers: [
        TitleChangerResolver,
        TitleChangerService,
        DatePipe,
        ToasterService,
        { provide: MAT_DATE_LOCALE, useValue: 'en-IN' },
        UserService,
        HttpWrapperService,
        URLService,
        VendorOnBoardingService,
        BillsOnBoardingService,
        MenuOptionsService,
        AppSideNavBarService,
        PageService,
        httpInterceptorProviders,
        SignupService,
        InvoiceService,
        GstPaymentService,
        BeneficiaryService,
        SubscriptionService,
        UtilityPaymentService,
        ComplaintService,
        CompanyPGService,
        PrepaidService,
        CompanyConfigService,
        CompanyPricingService,
        FilterModalService,
        CreditProductService,
        PurchaseOrderService,
        FundTransferService,
        ConnectedBankingService,
        PaymentRequestService,
        { provide: BsDatepickerConfig, useFactory: getDatepickerConfig },
        EnumApiService,
        ConfigApiService,
        EnkashCardsService,
        ProfileSetupServiceService,
        GstinService,
        CardApplicationDetailsService,
        DashboardCardsService,
        AddCardService,
        ProductGuideService,
        LocalStorageService,
        NavigationService,
        ResponsiveService,
        { provide: MAT_DATE_LOCALE, useValue: 'en-IN' },
        { provide: 'googleTagManagerId', useValue: environment.googleTagManagerId },
        {
            provide: APP_INITIALIZER,
            useFactory: (sites: UserService) => () => sites.validateToken(),
            deps: [UserService],
            multi: true
        },
        { provide: LOCALE_ID, useValue: "en-IN" },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {
  constructor() {
    registerLocaleData(localeEnIN);
  }
}
